import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, CalendarCheck, LayoutDashboard, Users, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";
import { fmtCents } from "../../lib/format";

const VER_CHIP = {
  admin_review_required: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  more_info_required: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
  email_verification_required: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

// Business-verification review queue: approve / reject / request more info.
function VerificationsTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState({});
  const load = useCallback(() => api.adminCommercialVerifications().then((r) => setItems(r.verifications)).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const review = async (v, action) => {
    setBusy(v.user_id + action);
    try {
      await api.adminCommercialVerificationReview(v.user_id, action, notes[v.user_id] || undefined);
      toast.success(`${v.business_name}: ${action === "more_info" ? "more info requested" : `${action}d`}`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  if (!items) return <div className="card-dark h-48 animate-pulse" />;
  if (!items.length) return <div className="card-dark p-8 text-center text-sm text-zinc-500" data-testid="verifications-empty">No verifications waiting for review.</div>;
  return (
    <div className="space-y-3" data-testid="verifications-list">
      {items.map((v) => (
        <div key={v.user_id} className="card-dark p-5 space-y-3" data-testid={`verification-row-${v.user_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-sm">{v.business_name}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{v.business_email} · account {v.account_email} · {v.properties_count} {v.properties_count === 1 ? "property" : "properties"}</p>
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-widest border rounded-full px-2.5 py-1 ${VER_CHIP[v.status] || ""}`} data-testid={`verification-status-${v.user_id}`}>
              {v.status.replace(/_/g, " ")}
            </span>
          </div>
          <p className="text-[11px] text-zinc-500">
            Email verified: {v.email_verified ? "yes" : "no"} · Domain: {v.domain} · Domain/name match: {v.domain_match ? "yes" : "no"} · Submitted: {v.submitted_at ? new Date(v.submitted_at).toLocaleString() : "—"}
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <input value={notes[v.user_id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [v.user_id]: e.target.value }))}
                   placeholder="Review note (optional)" aria-label="Review note" data-testid={`verification-note-${v.user_id}`}
                   className="flex-1 min-w-48 rounded-xl border border-[#27272A] bg-[#0A0D10] px-3 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-[#E50914]" />
            <button onClick={() => review(v, "approve")} disabled={!!busy} className="btn-secondary !py-2 !px-3 text-xs !text-emerald-400" data-testid={`verification-approve-${v.user_id}`}>Approve</button>
            <button onClick={() => review(v, "more_info")} disabled={!!busy} className="btn-secondary !py-2 !px-3 text-xs" data-testid={`verification-moreinfo-${v.user_id}`}>Request info</button>
            <button onClick={() => review(v, "reject")} disabled={!!busy} className="btn-secondary !py-2 !px-3 text-xs !text-red-400" data-testid={`verification-reject-${v.user_id}`}>Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const STATE_CHIP = {
  requested: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  suspended: "bg-red-500/15 text-red-400 border-red-500/30",
};
const PROP_CHIP = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  draft: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  unpublished: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  suspended: "bg-red-500/15 text-red-400 border-red-500/30",
};

const TABS = [["access", "Access", Users], ["overview", "Overview", LayoutDashboard], ["properties", "Properties", Building2], ["bookings", "Bookings", CalendarCheck], ["verifications", "Verifications", ShieldCheck]];

// Admin Commercial Master Console (V2 P2): access lifecycle, portfolio overview,
// property management (suspend/restore/unpublish — audited), booking oversight.
export default function AdminCommercial() {
  const [tab, setTab] = useState("access");
  return (
    <div data-testid="admin-commercial">
      <p className="section-label">Commercial</p>
      <h2 className="type-h3 mt-2">Commercial Master Console</h2>
      <div className="flex flex-wrap gap-1 p-1 rounded-xl border border-[#27272A] bg-[#0D1114]/80 w-fit mt-4" data-testid="admin-commercial-tabs" role="tablist">
        {TABS.map(([v, l, Icon]) => (
          <button key={v} role="tab" aria-selected={tab === v} data-testid={`admin-commercial-tab-${v}`} onClick={() => setTab(v)}
                  className={`px-3.5 py-1.5 rounded-lg text-[0.7rem] font-bold tracking-[0.1em] uppercase transition-colors inline-flex items-center gap-1.5 ${tab === v ? "bg-[#E50914] text-white" : "text-zinc-500 hover:text-zinc-200"}`}>
            <Icon className="w-3.5 h-3.5" />{l}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "access" && <AccessTab />}
        {tab === "overview" && <OverviewTab />}
        {tab === "properties" && <PropertiesTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "verifications" && <VerificationsTab />}
      </div>
    </div>
  );
}

function AccessTab() {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.adminCommercialRequests().then((r) => setUsers(r.users)).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const act = async (u, action) => {
    setBusy(u.user_id);
    try {
      await api.adminCommercialAccess(u.user_id, action);
      toast.success(`${u.email}: commercial ${action}${action === "grant" ? "ed" : action === "revoke" ? "d" : "ed"}`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  if (!users) return <div className="card-dark h-48 animate-pulse" />;
  const requested = users.filter((u) => u.commercial_access === "requested");
  const others = users.filter((u) => u.commercial_access !== "requested");
  const Row = ({ u }) => (
    <div className="card-dark p-5 flex flex-wrap items-center gap-4" data-testid={`commercial-user-${u.user_id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm truncate">{u.name || u.email}</p>
        <p className="text-xs text-zinc-500 truncate">{u.email}</p>
        {u.commercial_request?.business_name && <p className="text-xs text-zinc-400 mt-1">Business: {u.commercial_request.business_name}</p>}
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATE_CHIP[u.commercial_access] || ""}`} data-testid={`commercial-state-${u.user_id}`}>{u.commercial_access}</span>
      <div className="flex gap-2">
        {u.commercial_access !== "active" && <button onClick={() => act(u, "grant")} disabled={busy === u.user_id} className="btn-primary !py-2 !px-4 text-xs disabled:opacity-50" data-testid={`commercial-grant-${u.user_id}`}>Grant</button>}
        {u.commercial_access === "active" && <button onClick={() => act(u, "suspend")} disabled={busy === u.user_id} className="btn-secondary !py-2 !px-4 text-xs text-amber-400 disabled:opacity-50" data-testid={`commercial-suspend-${u.user_id}`}>Suspend</button>}
        <button onClick={() => act(u, "revoke")} disabled={busy === u.user_id} className="btn-secondary !py-2 !px-4 text-xs text-[#FF3B45] disabled:opacity-50" data-testid={`commercial-revoke-${u.user_id}`}>Remove</button>
      </div>
    </div>
  );
  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500 max-w-xl">Commercial Mode is granted manually. Removing access hides commercial navigation and returns 403 from commercial APIs; historical property records are preserved.</p>
      {requested.length > 0 && <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-400">Pending requests ({requested.length})</p>}
      {requested.map((u) => <Row key={u.user_id} u={u} />)}
      {others.length > 0 && <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 pt-3">Granted / suspended ({others.length})</p>}
      {others.map((u) => <Row key={u.user_id} u={u} />)}
      {users.length === 0 && <div className="card-dark p-10 text-center text-sm text-zinc-500" data-testid="commercial-requests-empty">No commercial access requests yet.</div>}
    </div>
  );
}

function OverviewTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.adminCommercialOverview().then(setD).catch((e) => toast.error(e.message)); }, []);
  if (!d) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="card-dark h-24 animate-pulse" />)}</div>;
  const cards = [
    ["Properties", d.properties_total], ["Active", d.properties_active], ["Live spots", d.spots_active],
    ["Bookings", d.bookings_total], ["Gross", fmtCents(d.gross_cents)], ["Service fees", fmtCents(d.service_fee_cents)],
    ["Tax", fmtCents(d.tax_cents)], ["Discounts", fmtCents(d.discount_cents)],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-commercial-overview">
      {cards.map(([l, v]) => (
        <div key={l} className="card-dark p-5">
          <p className="meta-label">{l}</p>
          <p className="font-display font-bold text-2xl mt-2">{v}</p>
        </div>
      ))}
    </div>
  );
}

function PropertiesTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (status) p.status = status;
    api.adminCommercialProperties(p).then(setData).catch((e) => toast.error(e.message));
  }, [q, status]);
  useEffect(() => { load(); }, [load]);
  const act = async (p, action) => {
    setBusy(p.property_id);
    try {
      const r = await api.adminCommercialPropertyStatus(p.property_id, action);
      toast.success(`${p.name} → ${r.status}`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, code, address…"
               className="rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2 text-sm text-white w-64" data-testid="admin-prop-search" aria-label="Search properties" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-[#27272A] bg-[#0A0D10] px-3 py-2 text-xs text-white" data-testid="admin-prop-status" aria-label="Filter by status">
          <option value="">All statuses</option>
          {["draft", "active", "unpublished", "suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="mt-4 space-y-3" data-testid="admin-properties-list">
        {!data && <div className="card-dark h-24 animate-pulse" />}
        {data && data.items.length === 0 && <div className="card-dark p-10 text-center text-sm text-zinc-500">No properties match.</div>}
        {data && data.items.map((p) => (
          <div key={p.property_id} className="card-dark p-5 flex flex-wrap items-center gap-4" data-testid={`admin-property-${p.property_code}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-sm">{p.name}</p>
                <span className="font-mono text-xs text-[#FF3B45]">{p.property_code}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${PROP_CHIP[p.status] || ""}`}>{p.status}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-1">{p.owner_email} · {p.address} · cap {p.capacity_total} · {p.spots_active} live spots · {p.bookings} bookings</p>
            </div>
            <div className="flex gap-2">
              {p.status === "active" && <button onClick={() => act(p, "unpublish")} disabled={busy === p.property_id} className="btn-secondary !py-2 !px-3 text-xs text-amber-400 disabled:opacity-50" data-testid={`admin-prop-unpublish-${p.property_code}`}>Unpublish</button>}
              {p.status !== "suspended" && <button onClick={() => act(p, "suspend")} disabled={busy === p.property_id} className="btn-secondary !py-2 !px-3 text-xs text-[#FF3B45] disabled:opacity-50" data-testid={`admin-prop-suspend-${p.property_code}`}>Suspend</button>}
              {p.status === "suspended" && <button onClick={() => act(p, "restore")} disabled={busy === p.property_id} className="btn-secondary !py-2 !px-3 text-xs text-emerald-400 disabled:opacity-50" data-testid={`admin-prop-restore-${p.property_code}`}>Restore</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BookingsTab() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const load = useCallback(() => {
    const p = { page };
    if (status) p.status = status;
    api.adminCommercialBookings(p).then(setData).catch((e) => toast.error(e.message));
  }, [status, page]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="rounded-xl border border-[#27272A] bg-[#0A0D10] px-3 py-2 text-xs text-white" data-testid="admin-booking-status" aria-label="Filter by status">
        <option value="">All statuses</option>
        {["pending", "pending_auth", "authorized", "paid", "cancelled", "expired", "refunded"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
      </select>
      <div className="mt-4 space-y-3" data-testid="admin-bookings-list">
        {!data && <div className="card-dark h-24 animate-pulse" />}
        {data && data.items.length === 0 && <div className="card-dark p-10 text-center text-sm text-zinc-500">No commercial bookings yet.</div>}
        {data && data.items.map((b) => (
          <div key={b.booking_id} className="card-dark p-5 flex flex-wrap items-center gap-4" data-testid={`admin-booking-${b.booking_id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm">{b.property_name || b.spot_title} <span className="text-zinc-500 font-normal">· {b.category_name}</span></p>
              <p className="text-xs text-zinc-500 mt-1 font-mono">{b.booking_id} · {b.property_spot_ref} · {new Date(b.start_time).toLocaleString()}</p>
            </div>
            <span className="text-xs text-zinc-400 uppercase tracking-wider">{b.status}</span>
            <p className="font-display font-bold">{fmtCents(b.total_cents, b.currency)}</p>
          </div>
        ))}
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between text-xs text-zinc-500 pt-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn-secondary !py-1.5 !px-3 disabled:opacity-40" data-testid="admin-bookings-prev">← Prev</button>
            <span>Page {data.page} of {data.pages}</span>
            <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="btn-secondary !py-1.5 !px-3 disabled:opacity-40" data-testid="admin-bookings-next">Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
